// File: frontend/src/components/ui/PageHeader.js
//
// Consistent page title block: title + optional description/status on the left,
// primary/secondary actions on the right. Stacks vertically on mobile so actions
// never crowd the title. All colours come from the theme.
//
// THE PAGE TITLE (§16). One heading level, one weight, one responsive size for
// every page: an <h1>, weight 800, 1.4rem → 1.7rem. `PageTitle` is that title on
// its own, for the few headers whose layout is richer than PageHeader's slots —
// Router Dashboard's freshness line, a page whose subtitle carries the station —
// so those pages still wear the same title without being forced into a layout
// that would cost them information. Anything that can use PageHeader should.

import React from "react";
import { Box, Typography, Stack } from "@mui/material";

export const PAGE_TITLE_SX = {
  fontSize: { xs: "1.4rem", sm: "1.7rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  color: "text.primary",
  lineHeight: 1.15,
  minWidth: 0,
  m: 0,
};

/** The canonical page title, alone. */
export function PageTitle({ children, sx }) {
  return (
    <Typography component="h1" sx={{ ...PAGE_TITLE_SX, ...sx }}>
      {children}
    </Typography>
  );
}

export default function PageHeader({
  title,
  description,
  status,          // optional node rendered next to the title (e.g. <StatusBadge/>, <ScopeChip/>)
  actions,         // optional node(s) — buttons, menus
  descriptionSx,   // e.g. hide the subtitle on a phone
  sx,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: { xs: 1.5, sm: 2 },
        mb: { xs: 2, sm: 2.5 },
        minWidth: 0,
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0, flexWrap: "wrap" }}>
          <PageTitle>{title}</PageTitle>
          {status}
        </Stack>
        {description && (
          <Typography sx={{ mt: 0.5, fontSize: { xs: 13, sm: 14 }, color: "text.secondary", maxWidth: 640, ...descriptionSx }}>
            {description}
          </Typography>
        )}
      </Box>

      {actions && (
        <Stack
          direction="row"
          gap={1}
          sx={{
            flexShrink: 0,
            flexWrap: "wrap",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          {actions}
        </Stack>
      )}
    </Box>
  );
}
